import React, { useState, useEffect } from 'react'
import { AiOutlineClose, AiOutlinePlus, AiOutlineSearch } from 'react-icons/ai';
import { FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/Auth';
import Swal from 'sweetalert2';

const AddGroupMember = ({groupId, showAddMemberModal, setShowAddMemberModal}) => {
  const { requestWithAuth } = useAuth();
  const [availableMembers, setAvailableMembers] = useState([]);
  
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [searchAvailable, setSearchAvailable] = useState("");


  const getInitials = (fullName) => {
    const names = fullName.split(' ');
    return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'elder':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'deacon':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'member':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };


  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member to add");
      return;
    }

    try {
      setAddingMembers(true);
      const response = await requestWithAuth(
        `/groups/group-members/${groupId}/?upload_type=Bulk`,
        'POST',
        { members: selectedMembers, role:"Member" }
      );

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `${response?.message}`
      }).then(() => {
        setShowAddMemberModal(false);
        setSelectedMembers([]);
        setSearchAvailable("");
      })
      
      
    } catch (error) {
      toast.error("Failed to add members to group");
    }finally{
        setAddingMembers(false);
    }
  };

  const fetchAvailableMembers = async () => {
    try {
      const response = await requestWithAuth(
        `/groups/available-members/${groupId}?search=${searchAvailable}`,
        'GET'
      );
      
      
      setAvailableMembers(response?.data);
    } catch (error) {
      console.error("Failed to fetch available members:", error);
      toast.error("Failed to load available members");
    }
  };

  useEffect(() => {
    if (searchAvailable?.length > 0) {
      fetchAvailableMembers();
    }
  }, [showAddMemberModal, searchAvailable]);


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Members to Group</h3>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedMembers([]);
                  setSearchAvailable("");
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <AiOutlineClose className="text-xl text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Search */}
              <div className="relative mb-4">
                <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search available members..."
                  value={searchAvailable}
                  onChange={(e) => setSearchAvailable(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Available Members List */}
              <div className="space-y-2 mb-6">
                {availableMembers?.length > 0 ? (
                  availableMembers.map((member) => (
                    <label
                      key={member.id}
                      className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMembers(prev => [...prev, member.id]);
                          } else {
                            setSelectedMembers(prev => prev.filter(id => id !== member.id));
                          }
                        }}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex items-center gap-3 flex-grow">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                          {getInitials(member.full_name)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.full_name}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(member.position)}`}>
                        {member.position}
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    {
                        searchAvailable?.length > 0 ? (
                        <p className="text-gray-500">
                            {searchAvailable ? 'No members found matching your search' : 'No available members to add'}
                        </p>
                        ):(
                            <p className="text-gray-500">
                                Try to search for a member first.
                            </p>
                        )
                    }
                    
                  </div>
                )}
              </div>

              {/* Selected Count */}
              {selectedMembers?.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    {selectedMembers.length} member{selectedMembers.length > 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedMembers([]);
                  setSearchAvailable("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMembers}
                disabled={selectedMembers.length === 0 || addingMembers}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addingMembers ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <AiOutlinePlus className="text-sm" />
                    Add {selectedMembers.length > 0 ? `${selectedMembers.length} ` : ''}Member{selectedMembers.length > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
  )
}

export default AddGroupMember